"use strict";
import { useQuery } from "@tanstack/react-query";
import { getBscContracts, getBscSubgraphs, hasBscSubgraph } from "constants/bscContracts";
import { useVeGaugeData } from "features/veSubgraph/gaugeData";
import { MAX_GAUGE_APR_PERCENT, computeV3GaugeApr } from "pages/Liquidity/utils/v3Apr";
import { useMemo } from "react";
import { MIN_STAKED_TVL_USD } from "services/cl-apr";
import { fetchFromSubgraph } from "utils/fetchSubgraph";
import { formatUnits } from "viem";
import { useReadContracts } from "wagmi";
import { calculatePoolLPAPR } from "./useGaugeData";
import { useTokenPriceMap } from "./useTokenPriceMap";
const RATIO_SCALE = 1000000000000000000n;
const CL_POOL_STAKED_LIQUIDITY_ABI = [
  {
    inputs: [],
    name: "stakedLiquidity",
    outputs: [{ name: "", type: "uint128" }],
    stateMutability: "view",
    type: "function"
  }
];
const V2_APR_PAIRS_QUERY = `
  query LiveAprPairs($ids: [ID!]!) {
    pairs(where: { id_in: $ids }, first: 1000) {
      id
      totalSupply
      reserveUSD
    }
  }
`;
const V3_APR_POOLS_QUERY = `
  query LiveAprPools($ids: [ID!]!) {
    pools(where: { id_in: $ids }, first: 1000) {
      id
      liquidity
      sqrtPrice
      tick
      totalValueLockedToken0
      totalValueLockedToken1
      totalValueLockedUSD
    }
  }
`;
const EMPTY_APR_MAP = /* @__PURE__ */ new Map();
export function computeLiveGaugeAprMap({
  gauges,
  v2PairsById,
  v3PoolsById,
  clStakedLiquidityByPool,
  topazPriceUsd,
  chainId,
  nowSeconds
}) {
  const aprByPool = /* @__PURE__ */ new Map();
  for (const gauge of gauges) {
    if (!gauge.isAlive) {
      continue;
    }
    const poolId = gauge.pool.id.toLowerCase();
    const rewardRatePerSecond = Number(formatUnits(BigInt(gauge.rewardRate), 18));
    const periodFinish = Number(gauge.periodFinish);
    const hasActiveRewards = rewardRatePerSecond > 0 && (periodFinish === 0 || periodFinish > nowSeconds);
    if (!hasActiveRewards) {
      aprByPool.set(poolId, 0);
      continue;
    }
    if (gauge.pool.poolType === "CL") {
      const pool = v3PoolsById.get(poolId);
      const stakedLiquidity = clStakedLiquidityByPool.get(poolId);
      if (!pool || stakedLiquidity === void 0) {
        continue;
      }
      let poolLiquidity;
      try {
        poolLiquidity = BigInt(pool.liquidity);
      } catch {
        continue;
      }
      const tvlValue = parseFloat(pool.totalValueLockedUSD || "0");
      let stakedTvlUsd2;
      if (poolLiquidity > 0n) {
        const ratioScaled = Number(stakedLiquidity * RATIO_SCALE / poolLiquidity) / Number(RATIO_SCALE);
        const boundedRatio = Math.min(Math.max(ratioScaled, 0), 1);
        stakedTvlUsd2 = tvlValue * boundedRatio;
      }
      if (stakedTvlUsd2 === void 0 || stakedTvlUsd2 <= 0) {
        aprByPool.set(poolId, 0);
        continue;
      }
      const { baseAprFraction, rangeAprFraction } = computeV3GaugeApr({
        pool: {
          id: poolId,
          token0: {
            id: gauge.pool.token0.id,
            symbol: gauge.pool.token0.symbol,
            name: gauge.pool.token0.name ?? gauge.pool.token0.symbol,
            decimals: gauge.pool.token0.decimals
          },
          token1: {
            id: gauge.pool.token1.id,
            symbol: gauge.pool.token1.symbol,
            name: gauge.pool.token1.name ?? gauge.pool.token1.symbol,
            decimals: gauge.pool.token1.decimals
          },
          liquidity: pool.liquidity,
          sqrtPrice: pool.sqrtPrice,
          tick: pool.tick,
          tickSpacingNumber: gauge.pool.tickSpacing ?? 0,
          totalValueLockedToken0: pool.totalValueLockedToken0,
          totalValueLockedToken1: pool.totalValueLockedToken1,
          totalValueLockedUSD: pool.totalValueLockedUSD
        },
        rewardRatePerSecond,
        stakedLiquidity,
        poolLiquidityRaw: poolLiquidity,
        stakedTvlUsd: stakedTvlUsd2,
        topazPriceUsd,
        chainId,
        rewardPeriodFinish: periodFinish,
        currentTimeSeconds: nowSeconds
      });
      if (rangeAprFraction !== void 0 && rangeAprFraction > 0) {
        aprByPool.set(poolId, rangeAprFraction * 100);
      } else if (baseAprFraction !== void 0 && baseAprFraction > 0) {
        aprByPool.set(poolId, Math.min(baseAprFraction * 100, MAX_GAUGE_APR_PERCENT));
      } else {
        aprByPool.set(poolId, 0);
      }
      continue;
    }
    const pair = v2PairsById.get(poolId);
    if (!pair) {
      continue;
    }
    let stakedLp;
    try {
      stakedLp = Number(formatUnits(BigInt(gauge.totalStaked), 18));
    } catch {
      continue;
    }
    const totalLpSupply = parseFloat(pair.totalSupply || "0");
    const reserveUsd = parseFloat(pair.reserveUSD || "0");
    if (!(stakedLp > 0) || !Number.isFinite(totalLpSupply) || totalLpSupply <= 0) {
      aprByPool.set(poolId, 0);
      continue;
    }
    const stakedRatio = Math.min(Math.max(stakedLp / totalLpSupply, 0), 1);
    const stakedTvlUsd = reserveUsd * stakedRatio;
    if (stakedTvlUsd < MIN_STAKED_TVL_USD) {
      aprByPool.set(poolId, 0);
      continue;
    }
    aprByPool.set(
      poolId,
      Math.min(
        calculatePoolLPAPR({ rewardRatePerSecond, stakedTvlUSD: stakedTvlUsd, topazPriceUSD: topazPriceUsd }),
        MAX_GAUGE_APR_PERCENT
      )
    );
  }
  return aprByPool;
}
export function useLiveGaugeAprMap(chainId, enabled = true) {
  const ve = useVeGaugeData(chainId, enabled);
  const { v2PoolIds, clPoolIds } = useMemo(() => {
    const v2 = [];
    const cl = [];
    ve.data?.gauges.forEach((gauge) => {
      if (!gauge.isAlive) {
        return;
      }
      const poolId = gauge.pool.id.toLowerCase();
      if (gauge.pool.poolType === "CL") {
        cl.push(poolId);
      } else {
        v2.push(poolId);
      }
    });
    return { v2PoolIds: v2.sort(), clPoolIds: cl.sort() };
  }, [ve.data]);
  const topazAddress = getBscContracts(chainId)?.topaz;
  const topazAddresses = useMemo(() => topazAddress ? [topazAddress] : [], [topazAddress]);
  const { priceMap } = useTokenPriceMap(chainId, topazAddresses);
  const topazPriceUsd = topazAddress ? priceMap.get(topazAddress.toLowerCase()) : void 0;
  const v2Query = useQuery({
    queryKey: ["live-gauge-apr-v2-pairs", chainId, v2PoolIds],
    queryFn: async () => {
      const data = await fetchFromSubgraph(getBscSubgraphs(chainId).v2, {
        query: V2_APR_PAIRS_QUERY,
        variables: { ids: v2PoolIds },
        requestId: `liveGaugeApr:v2:${chainId}`
      });
      return new Map((data?.pairs ?? []).map((pair) => [pair.id.toLowerCase(), pair]));
    },
    enabled: enabled && v2PoolIds.length > 0 && hasBscSubgraph(chainId, "v2"),
    staleTime: 3e4,
    refetchInterval: 6e4,
    gcTime: 5 * 6e4,
    retry: 2
  });
  const v3Query = useQuery({
    queryKey: ["live-gauge-apr-v3-pools", chainId, clPoolIds],
    queryFn: async () => {
      const data = await fetchFromSubgraph(getBscSubgraphs(chainId).v3, {
        query: V3_APR_POOLS_QUERY,
        variables: { ids: clPoolIds },
        requestId: `liveGaugeApr:v3:${chainId}`
      });
      return new Map((data?.pools ?? []).map((pool) => [pool.id.toLowerCase(), pool]));
    },
    enabled: enabled && clPoolIds.length > 0 && hasBscSubgraph(chainId, "v3"),
    staleTime: 3e4,
    refetchInterval: 6e4,
    gcTime: 5 * 6e4,
    retry: 2
  });
  const { data: clStakedData } = useReadContracts({
    contracts: clPoolIds.map((poolId) => ({
      address: poolId,
      abi: CL_POOL_STAKED_LIQUIDITY_ABI,
      functionName: "stakedLiquidity",
      chainId
    })),
    query: {
      enabled: enabled && clPoolIds.length > 0,
      staleTime: 3e4,
      refetchInterval: 6e4,
      gcTime: 5 * 6e4
    }
  });
  const clStakedLiquidityByPool = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    if (!clStakedData) {
      return map;
    }
    clPoolIds.forEach((poolId, index) => {
      const result = clStakedData[index];
      if (result?.status === "success" && typeof result.result === "bigint") {
        map.set(poolId, result.result);
      }
    });
    return map;
  }, [clStakedData, clPoolIds]);
  return useMemo(() => {
    const v2Ready = v2PoolIds.length === 0 || v2Query.data !== void 0;
    const v3Ready = clPoolIds.length === 0 || v3Query.data !== void 0 && clStakedData !== void 0;
    const isReady = ve.isActive && ve.data !== void 0 && topazPriceUsd !== void 0 && topazPriceUsd > 0 && v2Ready && v3Ready;
    if (!isReady || !ve.data) {
      return { aprByPool: EMPTY_APR_MAP, isReady: false };
    }
    const aprByPool = computeLiveGaugeAprMap({
      gauges: ve.data.gauges,
      v2PairsById: v2Query.data ?? /* @__PURE__ */ new Map(),
      v3PoolsById: v3Query.data ?? /* @__PURE__ */ new Map(),
      clStakedLiquidityByPool,
      topazPriceUsd,
      chainId,
      nowSeconds: Math.floor(Date.now() / 1e3)
    });
    return { aprByPool, isReady: true };
  }, [
    ve.isActive,
    ve.data,
    topazPriceUsd,
    v2Query.data,
    v3Query.data,
    clStakedData,
    clStakedLiquidityByPool,
    v2PoolIds.length,
    clPoolIds.length,
    chainId
  ]);
}
